import { z } from "zod";
import { optionalUuid } from "./_helpers/optional";
import { csvUuids } from "./common.schema";
import { ExternalIdEntrySchema } from "./external-id.schema";

export const jobStatuses = [
  "new",
  "scheduled",
  "in_progress",
  "completed",
  "canceled",
] as const;

export const JobSchema = z.object({
  id: z.string().uuid().describe("ID"),
  business_id: z.string().uuid().nullable().describe("Business"),
  account_id: z.string().uuid().nullable().describe("Account"),
  brand_id: z.string().uuid().nullable().describe("Brand"),
  proposal_id: z.string().uuid().nullable().describe("Proposal"),
  contact_role_id: z.string().uuid().nullable().describe("Contact Role"),
  job_number: z.string().nullable().describe("Job Number"),
  name: z.string().nullable().describe("Name"),
  job_type: z.string().nullable().describe("Job Type"),
  status: z.enum(jobStatuses).nullable().describe("Status"),
  scheduled_date: z.string().nullable().describe("Scheduled Date"),
  scheduled_start_time: z.string().nullable().describe("Scheduled Start Time"),
  scheduled_end_time: z.string().nullable().describe("Scheduled End Time"),
  estimated_hours: z.number().nullable().describe("Estimated Hours"),
  production_start_date: z
    .string()
    .nullable()
    .describe("Production Start Date"),
  production_end_date: z.string().nullable().describe("Production End Date"),
  approved_date: z.string().nullable().describe("Approved Date"),
  started_at: z.string().nullable().describe("Started At"),
  completed_at: z.string().nullable().describe("Completed At"),
  actual_hours: z.number().nullable().describe("Actual Hours"),
  assigned_crew_id: z.string().uuid().nullable().describe("Assigned Crew"),
  lead_tech_role_id: z.string().uuid().nullable().describe("Lead Tech"),
  // ADR-0048 (ADV-1177): maintenance visit jobs generated from an agreement
  // cadence link back to finance.service_agreements.
  service_agreement_id: z
    .string()
    .uuid()
    .nullable()
    .optional()
    .describe("Service Agreement"),
  description: z.string().nullable().optional().describe("Description"),
  notes: z.string().nullable().optional().describe("Notes"),
  completion_notes: z.string().nullable().describe("Completion Notes"),
  completion_photos: z
    .array(z.string())
    .nullable()
    .describe("Completion Photos"),
  customer_signoff_at: z.string().nullable().describe("Customer Signoff At"),
  customer_signoff_signature: z
    .record(z.unknown())
    .nullable()
    .describe("Customer Signoff Signature"),
  quality_checklist: z
    .record(z.unknown())
    .nullable()
    .describe("Quality Checklist"),
  quality_score: z.number().nullable().describe("Quality Score"),
  total_cost: z.number().nullable().describe("Total Cost"),
  total_revenue: z.number().nullable().describe("Total Revenue"),
  margin: z.number().nullable().optional().describe("Margin"),
  amount_paid: z.number().nullable().describe("Amount Paid"),
  accounts_receivable: z
    .number()
    .nullable()
    .optional()
    .describe("Accounts Receivable"),
  photo_urls: z.array(z.string()).default([]).describe("Photo URLs"),
  project_type: z.string().nullable().describe("Project Type"),
  collections_status: z.string().nullable().describe("Collections Status"),
  is_self_generated_lead: z
    .boolean()
    .nullable()
    .describe("Self Generated Lead"),
  specifications: z.record(z.unknown()).nullable().describe("Specifications"),
  custom_fields: z
    .record(z.unknown())
    .nullable()
    .optional()
    .describe("Custom Fields"),
  salesperson_user_id: z
    .string()
    .uuid()
    .nullable()
    .optional()
    .describe("Salesperson"),
  crew_chief_user_id: z
    .string()
    .uuid()
    .nullable()
    .optional()
    .describe("Crew Chief"),
  assigned_rep_name: z.string().nullable().optional().describe("Sales Rep"),
  crew_name: z.string().nullable().optional().describe("Crew Name"),
  has_crew_conflict: z
    .boolean()
    .nullable()
    .optional()
    .describe("Crew Conflict"),
  created_at: z.string().nullable().describe("Created At"),
  updated_at: z.string().nullable().describe("Updated At"),
  external_ids: ExternalIdEntrySchema.array()
    .optional()
    .describe("External IDs"),
});
export type Job = z.infer<typeof JobSchema>;

export const JobInsertSchema = z.object({
  proposal_id: optionalUuid().describe("Proposal"),
  contact_role_id: z.string().uuid().describe("Contact Role"),
  job_number: z.string().describe("Job Number"),
  name: z.string().optional().describe("Name"),
  job_type: z.string().optional().describe("Job Type"),
  status: z
    .enum(jobStatuses)
    .default("new")
    .or(z.literal("").transform(() => undefined))
    .describe("Status"),
  scheduled_date: z.string().optional().describe("Scheduled Date"),
  scheduled_start_time: z.string().optional().describe("Scheduled Start Time"),
  scheduled_end_time: z.string().optional().describe("Scheduled End Time"),
  estimated_hours: z.number().optional().describe("Estimated Hours"),
  production_start_date: z
    .string()
    .optional()
    .describe("Production Start Date"),
  production_end_date: z.string().optional().describe("Production End Date"),
  approved_date: z.string().optional().describe("Approved Date"),
  assigned_crew_id: optionalUuid().describe("Assigned Crew"),
  lead_tech_role_id: optionalUuid().describe("Lead Tech"),
  service_agreement_id: optionalUuid().describe("Service Agreement"),
  total_cost: z.number().optional().describe("Total Cost"),
  total_revenue: z.number().optional().describe("Total Revenue"),
  amount_paid: z.number().optional().describe("Amount Paid"),
  description: z.string().optional().describe("Description"),
  notes: z.string().optional().describe("Notes"),
  photo_urls: z.array(z.string()).optional().describe("Photo URLs"),
  project_type: z.string().optional().describe("Project Type"),
  collections_status: z.string().optional().describe("Collections Status"),
  is_self_generated_lead: z
    .boolean()
    .optional()
    .describe("Self Generated Lead"),
  specifications: z.any().optional().describe("Specifications"),
  custom_fields: z.any().optional().describe("Custom Fields"),
  brand_id: optionalUuid().describe("Brand"),
  salesperson_user_id: z
    .string()
    .uuid()
    .nullable()
    .optional()
    .describe("Salesperson"),
  crew_chief_user_id: z
    .string()
    .uuid()
    .nullable()
    .optional()
    .describe("Crew Chief"),
});

export const JobUpdateSchema = JobInsertSchema.partial().extend({
  started_at: z.string().optional().describe("Started At"),
  completed_at: z.string().optional().describe("Completed At"),
  actual_hours: z.number().optional().describe("Actual Hours"),
  completion_notes: z.string().optional().describe("Completion Notes"),
  completion_photos: z
    .array(z.string())
    .optional()
    .describe("Completion Photos"),
  customer_signoff_at: z.string().optional().describe("Customer Signoff At"),
  customer_signoff_signature: z
    .any()
    .optional()
    .describe("Customer Signoff Signature"),
  quality_checklist: z.any().optional().describe("Quality Checklist"),
  quality_score: z.number().optional().describe("Quality Score"),
});

export const JobQuerySchema = z.object({
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  status: z.enum(jobStatuses).optional(),
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  contact_role_id: z.string().uuid().optional(),
  // Legacy alias for opportunity_id — the jobs service maps this onto the
  // opportunity_id column (see packages/services/src/ops/jobs.ts). The
  // opportunity-detail page still uses this query name; keep until ADV-463
  // follow-up renames the alias on the client.
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  deal_id: z.string().uuid().optional(),
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  proposal_id: z.string().uuid().optional(),
  job_type: z.string().optional(),
  scheduled_date: z.string().optional(),
  date_from: z
    .string()
    .optional()
    .describe("Filter scheduled_date >= this date (YYYY-MM-DD)"),
  date_to: z
    .string()
    .optional()
    .describe("Filter scheduled_date <= this date (YYYY-MM-DD)"),
  search: z.string().optional().describe("Search job_number or name"),
  // ADV-782: filter to jobs whose assigned crew belongs to a given territory.
  // ADV-1350: now accepts CSV of territory UUIDs (backward compat: single UUID still works).
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  territory_id: csvUuids().optional(),
  // ADV-1350: multi-select crew filter (public param name crew_id → assigned_crew_id column)
  // schema-check-ignore: query-filter
  crew_id: csvUuids().optional(),
  // ADV-1350: multi-select salesperson filter
  // schema-check-ignore: query-filter
  salesperson_user_id: csvUuids().optional(),
});

// === Job Items ===

export const JobItemSchema = z.object({
  id: z.string().uuid().describe("ID"),
  job_id: z.string().uuid().describe("Job"),
  product_id: z.string().uuid().nullable().describe("Product"),
  name: z.string().nullable().describe("Name"),
  description: z.string().nullable().describe("Description"),
  category: z.string().nullable().describe("Category"),
  quantity: z.number().describe("Quantity"),
  unit_type: z.string().nullable().describe("Unit Type"),
  unit_cost: z.number().nullable().describe("Unit Cost"),
  labor_unit_cost: z.number().nullable().describe("Labor Unit Cost"),
  unit_price: z.number().nullable().describe("Unit Price"),
  line_total: z.number().nullable().optional().describe("Line Total"),
  specifications: z.record(z.unknown()).nullable().describe("Specifications"),
  custom_fields: z
    .record(z.unknown())
    .nullable()
    .optional()
    .describe("Custom Fields"),
  sort_order: z.number().nullable().describe("Sort Order"),
  notes: z.string().nullable().describe("Notes"),
  created_at: z.string().nullable().describe("Created At"),
  updated_at: z.string().nullable().describe("Updated At"),
});
export type JobItem = z.infer<typeof JobItemSchema>;

export const JobItemInsertSchema = z.object({
  product_id: optionalUuid().describe("Product"),
  name: z.string().optional().describe("Name"),
  description: z.string().optional().describe("Description"),
  category: z.string().optional().describe("Category"),
  quantity: z.number().default(1).describe("Quantity"),
  unit_type: z.string().optional().describe("Unit Type"),
  unit_cost: z.number().optional().describe("Unit Cost"),
  labor_unit_cost: z.number().optional().describe("Labor Unit Cost"),
  unit_price: z.number().optional().describe("Unit Price"),
  specifications: z.any().optional().describe("Specifications"),
  custom_fields: z.any().optional().describe("Custom Fields"),
  sort_order: z.number().optional().describe("Sort Order"),
  notes: z.string().optional().describe("Notes"),
});

export const JobItemUpdateSchema = JobItemInsertSchema.partial();
